import { useEffect, useState } from "react";
import { PreviewButton } from "@/components/PdfPreview";
import { toast } from "sonner";
import { UserPlus, MapPin, Loader2, FileDown, Trash2, ScanFace } from "lucide-react";
import api, { errMsg, download } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StoredImg } from "@/components/Uploads";
import { CameraCapture } from "@/components/tech/CameraCapture";

const today = () => new Date().toISOString().slice(0, 10);
const gps = () => new Promise((ok) => navigator.geolocation ? navigator.geolocation.getCurrentPosition((p) => ok(p.coords), () => ok(null), { enableHighAccuracy: true, timeout: 10000 }) : ok(null));

const HelperDialog = ({ open, onClose, onSaved }) => {
  const [h, setH] = useState({ name: "", phone: "", face_path: "" });
  const save = async () => {
    try { await api.post("/tech/helpers", h); toast.success("Helper terdaftar"); setH({ name: "", phone: "", face_path: "" }); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} data-testid="helper-dialog">
        <DialogHeader><DialogTitle className="font-heading">Daftarkan Helper</DialogTitle></DialogHeader>
        <Input placeholder="Nama helper" value={h.name} onChange={(e) => setH({ ...h, name: e.target.value })} data-testid="helper-name-input" />
        <Input placeholder="No. HP (opsional)" value={h.phone} onChange={(e) => setH({ ...h, phone: e.target.value })} data-testid="helper-phone-input" />
        <p className="text-xs text-slate-500">Foto wajah terdaftar (wajah terlihat jelas, tanpa masker/kacamata hitam)</p>
        <CameraCapture onDone={(p) => setH((o) => ({ ...o, face_path: p }))} testId="helper-camera" />
        <Button disabled={!h.name.trim() || !h.face_path} onClick={save} data-testid="helper-save-btn">Simpan Helper</Button>
      </DialogContent>
    </Dialog>
  );
};

const CheckIn = ({ helpers, onDone }) => {
  const [hid, setHid] = useState("");
  const [kind, setKind] = useState("in");
  const [photo, setPhoto] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const c = await gps();
      if (!c) toast.warning("Lokasi GPS tidak didapat, absen tetap dicatat tanpa lokasi");
      const { data } = await api.post("/tech/attendance", { helper_id: hid, kind, photo_path: photo, lat: c?.latitude, lng: c?.longitude, accuracy: c?.accuracy });
      toast.success(`${data.helper_name} absen ${kind === "in" ? "masuk" : "pulang"} ${data.time} (wajah cocok ${data.confidence}%)`);
      setPhoto(""); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-premium space-y-3 p-4" data-testid="checkin-card">
      <p className="font-heading font-semibold flex items-center gap-2"><ScanFace className="h-5 w-5 text-blue-800" />Absen Helper (Wajah + Lokasi)</p>
      <select className="h-10 w-full rounded-md border px-2 text-sm" value={hid} onChange={(e) => setHid(e.target.value)} data-testid="checkin-helper-select">
        <option value="">Pilih helper…</option>{helpers.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
      </select>
      <div className="flex gap-2">{[["in", "Masuk"], ["out", "Pulang"]].map(([v, l]) => <Button key={v} type="button" size="sm" variant={kind === v ? "default" : "outline"} onClick={() => setKind(v)} data-testid={`checkin-kind-${v}`}>{l}</Button>)}</div>
      <CameraCapture key={photo ? "has" : "none"} onDone={setPhoto} testId="checkin-camera" />
      <Button className="w-full" disabled={!hid || !photo || busy} onClick={submit} data-testid="checkin-submit-btn">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <MapPin className="h-4 w-4 mr-1" />}{busy ? "Memverifikasi wajah…" : "Kirim Absen"}</Button>
    </div>
  );
};

export const Attendance = () => {
  const [helpers, setHelpers] = useState([]);
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState({ date_from: today(), date_to: today() });
  const [add, setAdd] = useState(false);
  const loadH = () => api.get("/tech/helpers").then((r) => setHelpers(r.data));
  const load = () => api.get("/tech/attendance", { params: range }).then((r) => setRows(r.data));
  useEffect(() => { loadH(); }, []);
  useEffect(() => { load(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps
  const delH = async (h) => { if (window.confirm(`Hapus helper ${h.name}?`)) { await api.delete(`/tech/helpers/${h.id}`); loadH(); } };
  return (
    <div className="grid gap-4 lg:grid-cols-[360px_1fr]" data-testid="attendance-tab">
      <div className="space-y-4">
        <CheckIn helpers={helpers} onDone={load} />
        <div className="card-premium p-4">
          <div className="mb-2 flex items-center justify-between"><p className="font-semibold">Helper Terdaftar</p><Button size="sm" variant="outline" onClick={() => setAdd(true)} data-testid="helper-add-btn"><UserPlus className="h-4 w-4 mr-1" />Daftar</Button></div>
          {helpers.map((h) => (
            <div key={h.id} className="flex items-center gap-3 border-t py-2" data-testid={`helper-${h.id}`}>
              <StoredImg path={h.face_path} className="h-9 w-9 rounded-full object-cover" /><span className="flex-1 text-sm">{h.name}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => delH(h)} data-testid={`helper-delete-${h.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </div>
      <div className="card-premium p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <Input type="date" className="w-40" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} data-testid="att-from-input" />
          <Input type="date" className="w-40" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} data-testid="att-to-input" />
          <PreviewButton url="/tech/attendance/pdf" params={range} filename={`Absensi-Helper-${range.date_from}.pdf`} title="Absensi Helper" testId="att-preview-btn" />
          <Button variant="outline" onClick={() => download("/tech/attendance/pdf", range, `Absensi-Helper-${range.date_from}.pdf`)} data-testid="att-pdf-btn"><FileDown className="h-4 w-4 mr-1" />Report PDF</Button>
        </div>
        <div className="space-y-2" data-testid="att-list">
          {!rows.length && <p className="py-6 text-center text-sm text-slate-400">Belum ada absen pada periode ini.</p>}
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-lg border p-2 text-sm" data-testid={`att-${r.id}`}>
              <StoredImg path={r.photo_path} className="h-12 w-12 rounded-lg object-cover" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{r.helper_name} <span className={`ml-1 rounded px-1.5 text-[10px] ${r.kind === "in" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{r.kind === "in" ? "Masuk" : "Pulang"}</span></p>
                <p className="text-xs text-slate-500">{r.date} • {r.time} WIB • wajah cocok {r.confidence}% • oleh {r.by_name}</p>
              </div>
              {r.lat != null && <a className="text-xs text-blue-700 underline" href={`https://maps.google.com/?q=${r.lat},${r.lng}`} target="_blank" rel="noreferrer" data-testid={`att-map-${r.id}`}><MapPin className="inline h-3.5 w-3.5" />Lokasi</a>}
            </div>
          ))}
        </div>
      </div>
      <HelperDialog open={add} onClose={() => setAdd(false)} onSaved={() => { setAdd(false); loadH(); }} />
    </div>
  );
};
